import gsap from 'gsap';
import { refreshAnimations } from '../animations/general.js';

export function initTabs() {
  // Simple Tab System with URL Hash Support
  document.addEventListener('DOMContentLoaded', function() {
    // Get all tab buttons
    const tabButtons = document.querySelectorAll('.solutions__tabMenu-button');
    
    // Get all tab content items
    const tabContents = document.querySelectorAll('.solutions__tabContent-item');
    
    // Get the active indicator
    const indicator = document.querySelector('.solutions__tabMenu-active-indicator');
    const dropdown = document.querySelector('.solutions__tabDropdown');
    const dropdownToggle = dropdown ? dropdown.querySelector('.solutions__tabDropdown-toggle') : null;
    const dropdownLabel = dropdown ? dropdown.querySelector('.solutions__tabDropdown-label') : null;
    
    if (tabButtons.length === 0 || tabContents.length === 0) {
      return;
    }
    
    // Check if we're on mobile (below 768px breakpoint)
    function isMobile() {
      return window.innerWidth < 768;
    }
    
    function closeDropdown() {
      if (!dropdown) {
        return;
      }
      
      dropdown.classList.remove('is-open');
      
      if (dropdownToggle) {
        dropdownToggle.setAttribute('aria-expanded', 'false');
      }
    }
    
    // Function to switch tabs
    function switchTab(targetTabId, updateUrl = false) {
      
      // Hide all content
      tabContents.forEach(content => {
        content.style.display = 'none';
        content.classList.remove('active');
      });
      
      // Remove active class from all buttons
      tabButtons.forEach(button => {
        button.classList.remove('active');
      });
      
      // Show target content with fade-up animation
      const targetContent = document.querySelector(`[data-tab-content="${targetTabId}"]`);
      if (targetContent) {
        // Set initial state for fade-up animation (matching general.js timing)
        gsap.set(targetContent, {
          y: 30,
          opacity: 0
        });
        
        // Show the content
        targetContent.style.display = 'flex';
        targetContent.classList.add('active');
        
        // Animate to final state (matching general.js: duration 0.6, ease 'power2.out')
        gsap.to(targetContent, {
          y: 0,
          opacity: 1,
          duration: 0.6,
          ease: 'power2.out',
          onComplete: () => {
            // Refresh ScrollTrigger after tab content animation completes
            // This ensures animation triggers are recalculated for newly visible content
            if (typeof refreshAnimations === 'function') {
              refreshAnimations();
            }
          }
        });
      }
      
      // Add active class to target button
      const targetButton = document.querySelector(`[data-tab="${targetTabId}"]`);
      if (targetButton) {
        targetButton.classList.add('active');
        
        if (dropdownLabel) {
          dropdownLabel.textContent = targetButton.textContent.trim();
        }
        
        if (indicator) {
          if (isMobile()) {
            indicator.style.opacity = '0';
          } else {
            const buttonRect = targetButton.getBoundingClientRect();
            const containerRect = targetButton.closest('.solutions__tabMenu').getBoundingClientRect();
            const left = Math.round(buttonRect.left - containerRect.left);
            const width = Math.round(buttonRect.width);
            const currentWidth = parseFloat(getComputedStyle(indicator).width) || 0;
            
            indicator.style.top = '50%';
            indicator.style.transform = 'translateY(-50%)';
            indicator.style.height = 'calc(100% - 10px)';
            
            if (currentWidth === 0) {
              const originalTransition = indicator.style.transition || getComputedStyle(indicator).transition;
              indicator.style.transition = 'none';
              indicator.style.width = width + 'px';
              indicator.style.left = left + 'px';
              void indicator.offsetHeight;
              indicator.style.transition = originalTransition;
            } else {
              indicator.style.width = width + 'px';
              indicator.style.left = left + 'px';
            }
            
            indicator.style.opacity = '1';
          }
        }
      }
      
      closeDropdown();
      
      // Only update URL hash if explicitly requested (user clicked a tab)
      if (updateUrl) {
        window.history.pushState(null, null, `#${targetTabId}`);
      }
    }
    
    // Function to scroll to tabMenu
    function scrollToSolutionsSection() {
      // Find the tabMenu or tabDropdown element (prefer dropdown on mobile, menu on desktop)
      const tabDropdown = document.querySelector('.solutions__tabDropdown');
      const tabMenu = document.querySelector('.solutions__tabMenu');
      const targetElement = tabDropdown || tabMenu;
      
      if (targetElement) {
        // Get header height for offset
        const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height-initial')) || 100;
        
        // Get the element's position relative to the document
        const elementRect = targetElement.getBoundingClientRect();
        const elementTop = elementRect.top + window.pageYOffset;
        
        // Calculate scroll position: element top minus header height, with a small buffer
        const offsetPosition = elementTop - headerHeight - 20; // 20px buffer for spacing
        
        window.scrollTo({
          top: Math.max(0, offsetPosition), // Ensure we don't scroll to negative position
          behavior: 'smooth'
        });
      }
    }
    
    // Function to handle URL hash on page load
    function handleUrlHash() {
      const hash = window.location.hash.substring(1); // Remove #
      
      if (hash) {
        // Check if hash matches any tab
        const matchingButton = document.querySelector(`[data-tab="${hash}"]`);
        if (matchingButton) {
          switchTab(hash);
          // Scroll to section after a brief delay to ensure tab is switched
          setTimeout(() => {
            scrollToSolutionsSection();
          }, 100);
          return;
        }
      }
      
      // If no valid hash, initialize with first tab
      const firstTabId = tabButtons[0].getAttribute('data-tab');
      switchTab(firstTabId);
    }
    
    if (dropdownToggle) {
      dropdownToggle.addEventListener('click', (event) => {
        if (!dropdown) {
          return;
        }
        
        // Stop event propagation to prevent document click handler from immediately closing
        event.stopPropagation();
        
        const isOpen = dropdown.classList.toggle('is-open');
        dropdownToggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      });
    }
    
    if (dropdown) {
      document.addEventListener('click', (event) => {
        // Check if click is outside the dropdown container
        // The dropdown contains both the toggle button and the menu
        const clickedInside = dropdown.contains(event.target);
        
        // Only close if dropdown is open and click is outside
        if (!clickedInside && dropdown.classList.contains('is-open')) {
          closeDropdown();
        }
      });
      
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          closeDropdown();
        }
      });
    }
    
    // Add click listeners to all tab buttons
    tabButtons.forEach(button => {
      button.addEventListener('click', function() {
        const tabId = this.getAttribute('data-tab');
        switchTab(tabId, true); // Pass true to update URL
      });
    });
    
    // Listen for hash changes (back/forward buttons)
    window.addEventListener('hashchange', function() {
      handleUrlHash();
    });
    
    // Handle clicks on anchor links that point to tab hashes
    // This handles both same-page navigation and cross-page navigation
    document.addEventListener('click', function(event) {
      const link = event.target.closest('a[href*="#"]');
      if (!link) return;
      
      const href = link.getAttribute('href');
      if (!href) return;
      
      // Check if link points to a tab hash on the current page
      const url = new URL(href, window.location.origin);
      const hash = url.hash.substring(1);
      
      // Only handle if it's a tab hash and we're on a page with tabs
      if (hash && document.querySelector(`[data-tab="${hash}"]`)) {
        // Check if it's the same page
        if (url.pathname === window.location.pathname || url.pathname + '/' === window.location.pathname || window.location.pathname === url.pathname + '/') {
          event.preventDefault();
          // Switch tab and scroll
          switchTab(hash, true);
          setTimeout(() => {
            scrollToSolutionsSection();
          }, 100);
        }
        // If different page, let the browser navigate normally
        // The handleUrlHash will catch it on page load
      }
    });
    
    // Update indicator position on window resize (for mobile/desktop switching)
    let resizeTimeout;
    window.addEventListener('resize', function() {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(function() {
        closeDropdown();
        
        if (!indicator) {
          return;
        }
        
        const activeButton = document.querySelector('.solutions__tabMenu-button.active');
        if (activeButton && !isMobile()) {
          const buttonRect = activeButton.getBoundingClientRect();
          const containerRect = activeButton.closest('.solutions__tabMenu').getBoundingClientRect();
          const left = Math.round(buttonRect.left - containerRect.left);
          const width = Math.round(buttonRect.width);
          
          indicator.style.top = '50%';
          indicator.style.transform = 'translateY(-50%)';
          indicator.style.height = 'calc(100% - 10px)';
          indicator.style.left = left + 'px';
          indicator.style.width = width + 'px';
          indicator.style.opacity = '1';
        } else {
          indicator.style.opacity = '0';
        }
      }, 100);
    });
    
    // Initialize based on URL hash or first tab
    handleUrlHash();
  });
}